import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SYNC_SCHEDULES, type SyncJobInput } from "@krut/shared";
import { api, unwrap } from "../api.ts";
import { Badge, Button, Card, cx, Dialog, ErrorText, Field, Input, PageHeader, Select, Spinner, Switch } from "../components/ui.tsx";
import { fmtUsd } from "../components/runtime.tsx";

type Data = Awaited<ReturnType<typeof load>>;
type Job = Data["jobs"][number];
const load = () => unwrap(api.admin.sync.$get());
const loadGroups = () => unwrap(api.admin.groups.$get());

const scheduleLabel = { manual: "Manual only", hourly: "Every hour", daily: "Every day", weekly: "Every week" } as const;
const runTone = { running: "accent", succeeded: "good", failed: "bad", stopped: "neutral" } as const;
const runLabel = { running: "Running", succeeded: "Done", failed: "Failed", stopped: "Stopped at a limit" } as const;
const when = (d: string | null) => (d ? new Date(d).toLocaleString() : "—");
const sourceLabel = (j: Job) => (j.source.kind === "composio" ? j.source.toolkits.join(" + ") : j.source.url.replace(/^https?:\/\//, ""));

export function SyncPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["sync"], queryFn: load, refetchInterval: (query) => (query.state.data?.jobs.some((j) => j.running) ? 4000 : 20000) });
  const groups = useQuery({ queryKey: ["groups"], queryFn: loadGroups });
  const [editing, setEditing] = useState<Job | "new" | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["sync"] });
  const act = useMutation({
    mutationFn: async (a: { kind: "run" | "approve" | "reject"; id: string }) => {
      const param = { param: { id: a.id } };
      if (a.kind === "run") return unwrap(api.admin.sync.jobs[":id"].run.$post(param));
      if (a.kind === "approve") return unwrap(api.admin.sync.jobs[":id"].approve.$post(param));
      return unwrap(api.admin.sync.jobs[":id"].reject.$post(param));
    },
    onSuccess: invalidate,
  });

  if (q.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  const groupName = (id: string) => groups.data?.find((g) => g.id === id)?.name ?? "deleted group";
  const proposed = q.data.jobs.filter((j) => j.status === "proposed");
  const jobs = q.data.jobs.filter((j) => j.status !== "proposed");

  return (
    <>
      <PageHeader
        title="Knowledge sync"
        description="Keep company memory up to date from your apps. Each job is an AI agent that reads a source — any connected app or MCP server — following your plain-English instructions, and saves what it finds. Sources are only ever read, never changed."
        actions={<Button variant="primary" onClick={() => setEditing("new")}>New sync job</Button>}
      />
      <ErrorText error={act.error} />

      {proposed.length > 0 && (
        <Card className="mb-6 border-amber-300 p-5">
          <h2 className="mb-3 text-sm font-semibold">Waiting for your approval</h2>
          <ul className="space-y-3">
            {proposed.map((j) => (
              <li key={j.id} className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1 basis-60">
                  <div className="font-medium">{j.name} <span className="text-sm font-normal text-[var(--muted)]">· {sourceLabel(j)} · {scheduleLabel[j.schedule]}</span></div>
                  <div className="text-sm text-[var(--muted)]">Proposed by {j.createdByEmail ?? "someone"} through their assistant</div>
                  <p className="mt-1 whitespace-pre-wrap text-sm">{j.instructions}</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => setEditing(j)}>Review</Button>
                  <Button variant="primary" disabled={act.isPending} onClick={() => act.mutate({ kind: "approve", id: j.id })}>Approve</Button>
                  <Button variant="danger" disabled={act.isPending} onClick={() => act.mutate({ kind: "reject", id: j.id })}>Reject</Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Connections enabled={q.data.composioEnabled} />

      <Card className="mt-6 overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="border-b border-[var(--line)] text-left text-[11px] uppercase tracking-[0.08em] whitespace-nowrap text-[var(--muted)]">
              <th className="px-4 py-3 font-medium">Job</th>
              <th className="px-4 py-3 font-medium">Visible to</th>
              <th className="px-4 py-3 font-medium">Last run</th>
              <th className="px-4 py-3 font-medium">Documents</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-[var(--muted)]">No sync jobs yet. Create one here, or ask your assistant: “keep company memory in sync with our Notion wiki”.</td></tr>
            )}
            {jobs.map((j) => (
              <tr key={j.id} className="border-b border-[var(--line)] last:border-0">
                <td className="px-4 py-3">
                  <button className="font-medium hover:underline" onClick={() => setDetail(j.id)}>{j.name}</button>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-[var(--muted)]">
                    <span>{sourceLabel(j)} · {scheduleLabel[j.schedule]}</span>
                    {j.status === "paused" && <Badge>Paused</Badge>}
                    {j.status === "rejected" && <Badge tone="bad">Rejected</Badge>}
                    {j.running && <Badge tone="accent">Running</Badge>}
                  </div>
                </td>
                <td className="px-4 py-3 text-[var(--muted)]">{j.groupIds.length ? j.groupIds.map(groupName).join(", ") : "Everyone"}</td>
                <td className="px-4 py-3">
                  {j.lastRun ? (
                    <>
                      <Badge tone={runTone[j.lastRun.status]}>{runLabel[j.lastRun.status]}</Badge>
                      <div className="mt-0.5 text-xs text-[var(--muted)]">{when(j.lastRun.startedAt)} · {j.lastRun.saved} saved · {fmtUsd(j.lastRun.costUsd)}</div>
                    </>
                  ) : (
                    <span className="text-[var(--muted)]">Never</span>
                  )}
                  {j.nextRunAt && j.status === "active" && <div className="text-xs text-[var(--muted)]">Next: {when(j.nextRunAt)}</div>}
                </td>
                <td className="px-4 py-3">{j.documents}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <Button disabled={j.running || act.isPending || j.status === "rejected"} onClick={() => act.mutate({ kind: "run", id: j.id })}>Run now</Button>
                    <Button onClick={() => setEditing(j)}>Edit</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {editing && (
        <JobEditor
          job={editing === "new" ? null : editing}
          models={q.data.models}
          groups={groups.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); invalidate(); }}
        />
      )}
      {detail && <JobDetail id={detail} onClose={() => { setDetail(null); invalidate(); }} />}
    </>
  );
}

/* ── Company connections ─────────────────────────────────────────────────── */

function Connections({ enabled }: { enabled: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["sync-connections"], queryFn: () => unwrap(api.admin.sync.connections.$get()), enabled });
  const [search, setSearch] = useState<string | null>(null);
  const toolkits = useQuery({ queryKey: ["admin-toolkits", search], queryFn: () => unwrap(api.admin.composio.toolkits.$get({ query: { q: search ?? "" } })), enabled: search !== null });
  const connect = useMutation({
    mutationFn: (toolkit: string) => unwrap(api.admin.sync.connections.$post({ json: { toolkit } })),
    onSuccess: (r) => {
      window.open(r.url, "_blank", "noopener");
      setSearch(null);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.admin.sync.connections[":id"].$delete({ param: { id } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["sync-connections"] }),
  });
  return (
    <Card className="p-5">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="flex-1">
          <h2 className="text-sm font-semibold">Company connections</h2>
          <p className="text-sm text-[var(--muted)]">App accounts sync jobs read with — connected once for the company, separate from anyone's personal apps. Use an account that can see everything you want synced.</p>
        </div>
        {enabled && <Button onClick={() => setSearch(search === null ? "" : null)}>{search === null ? "Connect an app" : "Cancel"}</Button>}
        {enabled && <Button onClick={() => void qc.invalidateQueries({ queryKey: ["sync-connections"] })}>Refresh</Button>}
      </div>
      {!enabled && <p className="text-sm text-amber-800">Composio isn't configured, so only custom MCP sources are available.</p>}
      {search !== null && (
        <div className="mb-4 rounded-lg border border-[var(--line)] p-3">
          <Input autoFocus placeholder="Search apps: notion, confluence, slack, google drive…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <div className="mt-2 grid max-h-64 gap-1 overflow-y-auto sm:grid-cols-2">
            {toolkits.data?.toolkits.map((t) => (
              <button key={t.slug} disabled={connect.isPending} onClick={() => connect.mutate(t.slug)} className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-[var(--panel-2)]">
                {t.logo && <img src={t.logo} alt="" className="h-5 w-5" />}
                <span className="font-medium">{t.name}</span>
              </button>
            ))}
          </div>
          <ErrorText error={connect.error} />
        </div>
      )}
      <ErrorText error={q.error ?? remove.error} />
      <div className="flex flex-wrap gap-2">
        {q.data?.accounts.length === 0 && <span className="text-sm text-[var(--muted)]">None yet.</span>}
        {q.data?.accounts.map((a) => (
          <div key={a.id} className="flex items-center gap-2 rounded-full border border-[var(--line)] py-1 pl-2 pr-1 text-sm">
            {a.logo && <img src={a.logo} alt="" className="h-4 w-4" />}
            <span>{a.name}</span>
            <Badge tone={a.status === "ACTIVE" ? "good" : "bad"}>{a.status === "ACTIVE" ? "Connected" : a.status.toLowerCase()}</Badge>
            <button className="rounded-full px-2 text-[var(--muted)] hover:bg-[var(--panel-2)] hover:text-red-700" title="Disconnect" onClick={() => confirm(`Disconnect the company ${a.name} account? Jobs using it will fail until it's reconnected.`) && remove.mutate(a.id)}>×</button>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ── Editor ──────────────────────────────────────────────────────────────── */

type Form = Omit<SyncJobInput, "source"> & { kind: "composio" | "mcp"; toolkits: string; url: string; headers: { name: string; value: string }[]; keepHeaders: string[] };

function JobEditor({ job, models, groups, onClose, onSaved }: { job: Job | null; models: Data["models"]; groups: { id: string; name: string }[]; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState<Form>(() => ({
    name: job?.name ?? "",
    kind: job?.source.kind ?? "composio",
    toolkits: job?.source.kind === "composio" ? job.source.toolkits.join(", ") : "",
    url: job?.source.kind === "mcp" ? job.source.url : "",
    headers: [],
    keepHeaders: job?.source.kind === "mcp" ? job.source.headerNames : [],
    instructions: job?.instructions ?? "",
    schedule: job?.schedule ?? "daily",
    groupIds: job?.groupIds ?? [],
    model: job?.model ?? null,
    maxDocumentsPerRun: job?.maxDocumentsPerRun ?? 200,
    maxCostPerRunUsd: job?.maxCostPerRunUsd ?? 1,
    enabled: job ? job.status !== "paused" : true,
  }));
  const set = (p: Partial<Form>) => setF({ ...f, ...p });
  const save = useMutation({
    mutationFn: async (): Promise<unknown> => {
      const newHeaders = f.headers.filter((h) => h.name.trim());
      const source =
        f.kind === "composio"
          ? { kind: "composio" as const, toolkits: f.toolkits.split(/[,\s+]+/).map((s) => s.trim().toLowerCase()).filter(Boolean) }
          : { kind: "mcp" as const, url: f.url.trim(), ...(newHeaders.length || !f.keepHeaders.length ? { headers: Object.fromEntries(newHeaders.map((h) => [h.name.trim(), h.value])) } : {}) };
      const json: SyncJobInput = { name: f.name, source, instructions: f.instructions, schedule: f.schedule, groupIds: f.groupIds, model: f.model, maxDocumentsPerRun: f.maxDocumentsPerRun, maxCostPerRunUsd: f.maxCostPerRunUsd, enabled: f.enabled };
      return job ? unwrap(api.admin.sync.jobs[":id"].$put({ param: { id: job.id }, json })) : unwrap(api.admin.sync.jobs.$post({ json }));
    },
    onSuccess: onSaved,
  });

  return (
    <Dialog open onClose={onClose} title={job ? `Edit ${job.name}` : "New sync job"} wide>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Field label="Name"><Input required value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder="Notion — Clients wiki" /></Field>
        <Field label="Source">
          <div className="mb-2 flex gap-2">
            {(["composio", "mcp"] as const).map((k) => (
              <button type="button" key={k} onClick={() => set({ kind: k })} className={cx("rounded-md border px-3 py-1.5 text-sm", f.kind === k ? "border-[var(--color-accent)] bg-[var(--panel-2)] font-medium" : "border-[var(--line)]")}>
                {k === "composio" ? "Connected app" : "Custom MCP server"}
              </button>
            ))}
          </div>
          {f.kind === "composio" ? (
            <Input required value={f.toolkits} onChange={(e) => set({ toolkits: e.target.value })} placeholder="notion" className="font-mono" />
          ) : (
            <div className="space-y-2">
              <Input required type="url" value={f.url} onChange={(e) => set({ url: e.target.value })} placeholder="https://mcp.example.com/mcp" className="font-mono" />
              {f.keepHeaders.length > 0 && !f.headers.length && <p className="text-xs text-[var(--muted)]">Keeps the stored {f.keepHeaders.join(", ")} header{f.keepHeaders.length > 1 ? "s" : ""}. Add headers below to replace them.</p>}
              {f.headers.map((h, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={h.name} placeholder="Authorization" onChange={(e) => set({ headers: f.headers.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} className="w-44 font-mono text-xs" />
                  <Input type="password" autoComplete="off" value={h.value} placeholder="Bearer …" onChange={(e) => set({ headers: f.headers.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) })} className="font-mono text-xs" />
                </div>
              ))}
              <button type="button" className="text-sm text-[var(--color-accent)] hover:underline" onClick={() => set({ headers: [...f.headers, { name: "", value: "" }] })}>+ Add a header (e.g. an API key)</button>
            </div>
          )}
        </Field>
        <Field label="Instructions" hint="What to sync and what to leave out, in plain English. The agent follows these on every run.">
          <textarea required rows={5} value={f.instructions} onChange={(e) => set({ instructions: e.target.value })} placeholder="Sync every page in the Clients and Engineering teamspaces. Skip meeting notes older than a year and anything titled Draft." className="w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 py-2 text-sm outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Schedule">
            <Select value={f.schedule} onChange={(e) => set({ schedule: e.target.value as Form["schedule"] })}>
              {SYNC_SCHEDULES.map((s) => <option key={s} value={s}>{scheduleLabel[s]}</option>)}
            </Select>
          </Field>
          <Field label="AI model" hint="Pick one that is good at using tools.">
            <Select value={f.model ?? ""} onChange={(e) => set({ model: e.target.value || null })}>
              <option value="">Default ({models[0]?.label ?? "none enabled"})</option>
              {models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </Select>
          </Field>
          <Field label="Max documents per run"><Input type="number" min={1} max={2000} value={f.maxDocumentsPerRun} onChange={(e) => set({ maxDocumentsPerRun: Number(e.target.value) })} /></Field>
          <Field label="Max AI spend per run (USD)"><Input type="number" min={0.01} step={0.01} max={100} value={f.maxCostPerRunUsd} onChange={(e) => set({ maxCostPerRunUsd: Number(e.target.value) })} /></Field>
        </div>
        <Field label="Who can recall this knowledge" hint={job && job.groupIds.join() !== f.groupIds.join() ? "Changing this re-saves every document on the next run." : "Everyone, unless you pick groups."}>
          <div className="flex flex-wrap gap-2">
            {groups.map((g) => {
              const on = f.groupIds.includes(g.id);
              return (
                <button type="button" key={g.id} onClick={() => set({ groupIds: on ? f.groupIds.filter((x) => x !== g.id) : [...f.groupIds, g.id] })} className={cx("rounded-full border px-3 py-1 text-sm", on ? "border-[var(--color-accent)] bg-[var(--panel-2)] font-medium" : "border-[var(--line)] text-[var(--muted)]")}>
                  {g.name}
                </button>
              );
            })}
          </div>
        </Field>
        {job?.status !== "proposed" && <Switch label="Enabled" hint="Paused jobs don't run on their schedule." checked={f.enabled} onChange={(v) => set({ enabled: v })} />}
        <ErrorText error={save.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={save.isPending}>Save</Button>
        </div>
      </form>
    </Dialog>
  );
}

/* ── Detail: runs, log, documents ───────────────────────────────────────── */

function JobDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useQuery({ queryKey: ["sync-job", id], queryFn: () => unwrap(api.admin.sync.jobs[":id"].$get({ param: { id } })), refetchInterval: (query) => (query.state.data?.job.running ? 3000 : false) });
  const [runId, setRunId] = useState<string | null>(null);
  const [removeDocs, setRemoveDocs] = useState(false);
  const del = useMutation({
    mutationFn: () => unwrap(api.admin.sync.jobs[":id"].$delete({ param: { id }, query: { removeDocuments: removeDocs ? "true" : "false" } })),
    onSuccess: onClose,
  });
  useEffect(() => {
    if (!runId && q.data?.runs[0]) setRunId(q.data.runs[0].id);
  }, [q.data, runId]);

  return (
    <Dialog open onClose={onClose} title={q.data?.job.name ?? "Sync job"} wide>
      {!q.data ? (
        <div className="grid place-items-center py-10"><Spinner /></div>
      ) : (
        <div className="space-y-5">
          <p className="whitespace-pre-wrap text-sm">{q.data.job.instructions}</p>
          {q.data.job.notes && (
            <div className="rounded-lg bg-[var(--panel-2)] px-3 py-2 text-xs">
              <div className="mb-1 font-medium text-[var(--muted)]">The agent's notes for its next run</div>
              <div className="whitespace-pre-wrap">{q.data.job.notes}</div>
            </div>
          )}
          <section>
            <h3 className="mb-2 text-sm font-semibold">Runs</h3>
            {q.data.runs.length === 0 ? (
              <p className="text-sm text-[var(--muted)]">No runs yet.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {q.data.runs.map((r) => (
                  <button key={r.id} onClick={() => setRunId(r.id)} className={cx("rounded-md border px-2 py-1 text-xs", runId === r.id ? "border-[var(--color-accent)] bg-[var(--panel-2)]" : "border-[var(--line)]")}>
                    {new Date(r.startedAt).toLocaleString()} · <span className={r.status === "failed" ? "text-red-700" : ""}>{runLabel[r.status]}</span>
                  </button>
                ))}
              </div>
            )}
            {runId && <RunLog id={runId} />}
          </section>
          <section>
            <h3 className="mb-2 text-sm font-semibold">Documents in company memory ({q.data.documents.length}{q.data.documents.length === 300 ? "+" : ""})</h3>
            <ul className="max-h-56 divide-y divide-[var(--line)] overflow-y-auto rounded-lg border border-[var(--line)] text-sm">
              {q.data.documents.length === 0 && <li className="px-3 py-2 text-[var(--muted)]">None yet.</li>}
              {q.data.documents.map((d) => (
                <li key={d.sourceId} className="flex gap-3 px-3 py-1.5">
                  <span className="min-w-0 flex-1 truncate">{d.url ? <a href={d.url} target="_blank" rel="noreferrer noopener" className="hover:underline">{d.title}</a> : d.title}</span>
                  <span className="shrink-0 text-xs text-[var(--muted)]">{new Date(d.syncedAt).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          </section>
          <section className="rounded-lg border border-red-200 p-3">
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex flex-1 items-center gap-2 text-sm">
                <input type="checkbox" checked={removeDocs} onChange={(e) => setRemoveDocs(e.target.checked)} />
                Also remove its {q.data.documents.length} documents from company memory
              </label>
              <Button variant="danger" disabled={del.isPending || q.data.job.running} onClick={() => confirm("Delete this sync job?") && del.mutate()}>Delete job</Button>
            </div>
            <ErrorText error={del.error} />
          </section>
        </div>
      )}
    </Dialog>
  );
}

const logTone: Record<string, string> = { error: "text-red-700", limit: "text-amber-800", save: "text-emerald-800", note: "text-[var(--muted)]", check: "text-[var(--muted)]", skip: "text-[var(--muted)]" };

function RunLog({ id }: { id: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["sync-run", id], queryFn: () => unwrap(api.admin.sync.runs[":id"].$get({ param: { id } })), refetchInterval: (query) => (query.state.data?.status === "running" ? 2500 : false) });
  const stop = useMutation({ mutationFn: () => unwrap(api.admin.sync.runs[":id"].stop.$post({ param: { id } })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["sync-run", id] }) });
  if (!q.data) return <div className="py-4"><Spinner /></div>;
  const r = q.data;
  return (
    <div className="mt-3 rounded-lg border border-[var(--line)]">
      <div className="flex flex-wrap items-center gap-3 border-b border-[var(--line)] px-3 py-2 text-xs">
        <Badge tone={runTone[r.status]}>{runLabel[r.status]}</Badge>
        <span className="text-[var(--muted)]">{r.trigger === "manual" ? "Started by hand" : "Scheduled"} · {r.steps} steps · {r.saved} saved · {r.unchanged} unchanged · {fmtUsd(r.costUsd)}</span>
        {r.status === "running" && <Button className="ml-auto" disabled={stop.isPending} onClick={() => stop.mutate()}>Stop</Button>}
      </div>
      {(r.summary || r.error) && <div className={cx("border-b border-[var(--line)] px-3 py-2 text-sm", r.error && "text-red-700")}>{r.error ?? r.summary}</div>}
      <ol className="max-h-72 overflow-y-auto px-3 py-2 font-mono text-[11px] leading-relaxed">
        {r.log.map((l, i) => (
          <li key={i} className={logTone[l.kind] ?? ""}>
            <span className="text-[var(--muted)]">{new Date(l.at).toLocaleTimeString()}</span> {l.text}
          </li>
        ))}
      </ol>
    </div>
  );
}
